import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import validator from "validator";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";
import MoneyGifterViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/MoneyGifterViewModel";
import OutstandingLoanViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/OutstandingLoanViewModel";
import CreditCardViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/CreditCardViewModel";
import FinancialCommitmentViewModle from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/FinancialCommitmentViewModel";

// Models
import { IncomeDetailsModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsModel";
import { IncomeDetailDTO } from "Globals/Models/DTO/IncomeDetailDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";

export default class IncomeDetailsViewModel extends FormSectionViewModelBase<IncomeDetailsModel> {
    @observable public applicantIncomeOverAllowedAmmount: boolean = false;

    @observable public moneyGifterViewModels: MoneyGifterViewModel[] = [];
    @observable public outstandingLoanViewModels: OutstandingLoanViewModel[] = [];
    @observable public creditCardViewModels: CreditCardViewModel[] = [];
    @observable public financialCommitmentViewModels: FinancialCommitmentViewModle[] = [];

    constructor(incomeDetailsModel: IncomeDetailsModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, incomeDetailsModel);
        this.saveStore.addCallback(FormSectionsEnum.IncomeDetails, this.save);
        this.saveFunction = this.save;
        this.createViewModels();
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.IncomeDetails, this.canProceed);
        }
    }

    @action
    private createViewModels(): void {
        if (this.model.moneyGifterModels) {
            for (const model of this.model.moneyGifterModels) {
                this.moneyGifterViewModels.push(new MoneyGifterViewModel(model));
            }
        }
        if (this.model.outstandingLoanModels) {
            for (const model of this.model.outstandingLoanModels) {
                this.outstandingLoanViewModels.push(new OutstandingLoanViewModel(model));
            }
        }
        if (this.model.creditCardModels) {
            for (const model of this.model.creditCardModels) {
                this.creditCardViewModels.push(new CreditCardViewModel(model));
            }
        }
        if (this.model.financialCommitmentModels) {
            for (const model of this.model.financialCommitmentModels) {
                this.financialCommitmentViewModels.push(new FinancialCommitmentViewModle(model));
            }
        }
    }

    @computed
    public get getSortedMoneyGifterViewModels(): MoneyGifterViewModel[] {
        return this.moneyGifterViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }
    public get getSortedOutstandingLoanViewModels(): OutstandingLoanViewModel[] {
        return this.outstandingLoanViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }
    public get getSortedCreditCardViewModels(): CreditCardViewModel[] {
        return this.creditCardViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }
    public get getSortedFinancialCommitmentViewModles(): FinancialCommitmentViewModle[] {
        return this.financialCommitmentViewModels
            .slice()
            .sort((a, b) => (b.model.createdDate ? new Date(b.model.createdDate).getTime() : 0) - (a.model.createdDate ? new Date(a.model.createdDate).getTime() : 0));
    }

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;

            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertIncomeDetails: IncomeDetailDTO = this.model.toDto();
                const apiResult = await this.Post<IncomeDetailDTO>(AppUrls.Server.ApplicationForm.Upsert.IncomeDetails, upsertIncomeDetails);

                if (apiResult.wasSuccessful) {
                    this.model.fromIncomeDetailDto(apiResult.payload);
                    console.info("IncomeDetails:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.IncomeDetails, this.canProceed);

                    // Upload stashed MoneyGifter forms to DB.
                    let gifterForms = this.moneyGifterViewModels.filter((a) => !a.model.incomeDetailId);
                    gifterForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    // Upload stashed OutstandingLoan forms to DB.
                    let loanForms = this.outstandingLoanViewModels.filter((a) => !a.model.incomeDetailId);
                    loanForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    // Upload stashed FinancialCommitment forms to DB.
                    let finacialForms = this.financialCommitmentViewModels.filter((a) => !a.model.incomeDetailId);
                    finacialForms.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    // Upload stashed CreditCard forms to DB.
                    let creditCardViewModels = this.creditCardViewModels.filter((a) => !a.model.incomeDetailId);
                    creditCardViewModels.map(async (c) => {
                        await c.saveFromStash(apiResult.payload.id as string);
                    });

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("IncomeDetails:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };

    @action
    public handleInputChange = (): void => {
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.IncomeDetails, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<IncomeDetailsModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleFieldChange = (fieldName: keyof FieldType<IncomeDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<IncomeDetailsModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<IncomeDetailsModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<IncomeDetailsModel>;
            let value = this.model.getValue(field);
            if (field == "moneyGifterModels") return;
            if (field == "outstandingLoanModels") return;
            if (field == "creditCardModels") return;
            if (field == "financialCommitmentModels") return;
            if (this.model.hasAdditionalMonthlyHouseholdIncome == false) {
                if (field == "workingTaxCredit") return;
                if (field == "childTaxCredit") return;
                if (field == "disabilityAllowance") return;
                if (field == "guaranteedMaintenanceIncome") return;
                if (field == "otherIncome") return;
            }
            if (this.isEmptyNullOrUndefined(value as any)) {
                completed = false;
            }
            if (completed == false) {
                // console.error("Field not completed: ", field, "Value: ", value);
            }
        });

        if (this.model.claimsGiftedMoney == true) {
            if (this.moneyGifterViewModels.length < 1) {
                completed = false;
            }
        }
        if (this.model.hasOutstandingLoans == true) {
            if (this.outstandingLoanViewModels.length < 1) {
                completed = false;
            }
        }
        if (this.model.hasCreditCards == true) {
            if (this.creditCardViewModels.length < 1) {
                completed = false;
            }
        }
        if (this.model.hasOtherRegularMonthlyFinancialCommitments == true) {
            if (this.financialCommitmentViewModels.length < 1) {
                completed = false;
            }
        }
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<IncomeDetailsModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<IncomeDetailsModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "grossAnnualIncome") {
            if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                if (value == null || validator.isEmpty(value.toString())) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
            if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                if (this.applicantIncomeOverAllowedAmmount == true) {
                    errorMessage = "Total applicants income cannot exceed £80,000";
                    isValid = false;
                }
            }
        }
        if (fieldName == "regularAnnualOvertimeBonusCommission") {
            if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                if (value == null || validator.isEmpty(value.toString())) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
            if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                if (this.applicantIncomeOverAllowedAmmount == true) {
                    errorMessage = "Total applicants income cannot exceed £80,000";
                    isValid = false;
                }
            }
        }
        if (fieldName == "workingTaxCredit") {
            if (this.model.hasAdditionalMonthlyHouseholdIncome) {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (fieldName == "childTaxCredit") {
            if (this.model.hasAdditionalMonthlyHouseholdIncome) {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (fieldName == "disabilityAllowance") {
            if (this.model.hasAdditionalMonthlyHouseholdIncome) {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (fieldName == "guaranteedMaintenanceIncome") {
            if (this.model.hasAdditionalMonthlyHouseholdIncome) {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (fieldName == "otherIncome") {
            if (this.model.hasAdditionalMonthlyHouseholdIncome) {
                if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                    if (value == null || validator.isEmpty(value.toString())) {
                        errorMessage = "required to proceed";
                        isValid = false;
                    }
                }
            }
        }
        if (fieldName == "totalSavings") {
            if ((value && !validator.isEmpty(value.toString())) || isRequired) {
                if (value == null || validator.isEmpty(value.toString())) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "claimsGiftedMoney") {
            if (isRequired) {
                if (value == true && this.moneyGifterViewModels.length < 1) {
                    errorMessage = "Please provide the details of the person gifting the deposit";
                    isValid = false;
                }
            }
        }
        if (fieldName == "currentRent") {
            isValid = true;
        }
        if (fieldName == "hasOutstandingLoans") {
            if (isRequired) {
                if (value == true && this.outstandingLoanViewModels.length < 1) {
                    errorMessage = "provide outstanding loan details";
                    isValid = false;
                }
            }
        }
        if (fieldName == "hasCreditCards") {
            if (isRequired) {
                if (value == true && this.creditCardViewModels.length < 1) {
                    errorMessage = "provide credit card details";
                    isValid = false;
                }
            }
        }
        if (fieldName == "hasOtherRegularMonthlyFinancialCommitments") {
            if (isRequired) {
                if (value == true && this.financialCommitmentViewModels.length < 1) {
                    errorMessage = "provide other financial commitment details";
                    isValid = false;
                }
            }
        }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<IncomeDetailsModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
